import { HttpClient } from '@sisense/sdk-rest-client';
import { getHttpClient } from '@/singleton-clients';

// REST build trigger + poller for Sisense Linux. The GraphQL `buildElasticube`
// mutation accepts but the build itself fails on this instance, so we use
// `POST /api/v2/builds` which every successful build observed on the cluster
// uses (see project_sisense_csv_upload_pitfalls memory).

interface BuildRecord {
	oid: string;
	datamodelId: string;
	status:
		| 'building'
		| 'pending'
		| 'done'
		| 'success'
		| 'failed'
		| 'error'
		| null;
	created?: string;
	completed?: string;
}

export async function triggerFullBuild(
	datamodelOid: string,
	httpClient?: HttpClient,
): Promise<{ buildOid: string }> {
	const client = httpClient ?? getHttpClient();
	try {
		const res = await client.post<BuildRecord>(`/api/v2/builds`, {
			datamodelId: datamodelOid,
			buildType: 'full',
			rowLimit: 0,
		});
		if (!res?.oid) {
			throw new Error('triggerFullBuild: no build oid returned');
		}
		return { buildOid: res.oid };
	} catch (err) {
		// 409 = build already in progress for this datamodel. Sisense rejects
		// the POST without echoing the in-flight build's oid, so we list
		// builds and pick the active one. Returning its oid lets the caller
		// poll the running build instead of surfacing a spurious failure to
		// the user.
		if (extractHttpStatus(err) === 409) {
			const inFlight = await findInFlightBuild(datamodelOid, client);
			if (inFlight) return { buildOid: inFlight };
		}
		throw err;
	}
}

async function findInFlightBuild(
	datamodelOid: string,
	client: HttpClient,
): Promise<string | undefined> {
	try {
		const builds = await client.get<BuildRecord[]>(`/api/v2/builds`);
		const match = builds?.find(
			(b) =>
				b?.datamodelId === datamodelOid &&
				(b.status === 'building' || b.status === 'pending'),
		);
		return match?.oid;
	} catch {
		return undefined;
	}
}

export interface PollBuildResult {
	status: 'done' | 'failed' | 'timeout';
	durationMs: number;
}

export async function pollBuild(
	buildOid: string,
	options: {
		intervalMs?: number;
		timeoutMs?: number;
		onUpdate?: (status: string) => void;
		httpClient?: HttpClient;
	} = {},
): Promise<PollBuildResult> {
	const intervalMs = options.intervalMs ?? 5_000;
	const timeoutMs = options.timeoutMs ?? 600_000;
	const client = options.httpClient ?? getHttpClient();
	const start = Date.now();
	const deadline = start + timeoutMs;
	let lastStatus = '';

	while (Date.now() < deadline) {
		try {
			const rec = await client.get<BuildRecord>(
				`/api/v2/builds/${buildOid}`,
			);
			const status = rec?.status ?? '';
			if (status && status !== lastStatus) {
				options.onUpdate?.(status);
				lastStatus = status;
			}
			if (status === 'done' || status === 'success') {
				return { status: 'done', durationMs: Date.now() - start };
			}
			if (status === 'failed' || status === 'error') {
				return { status: 'failed', durationMs: Date.now() - start };
			}
		} catch (err) {
			// Fail fast only on the small set of HTTP statuses that mean
			// "this buildOid will never become readable" — 400 (bad request,
			// permanently malformed oid) and 410 (gone). Everything else
			// (401/403 = token blip, 408/429 = transient, 404 = brief gap
			// between trigger and first readable poll) stays transient so we
			// don't kill the card while a real build is still chugging.
			const status = extractHttpStatus(err);
			if (status === 400 || status === 410) {
				return { status: 'failed', durationMs: Date.now() - start };
			}
			// transient / network / 5xx / 401 / 403 / 404 / 408 / 429 — keep polling
		}
		await sleep(intervalMs);
	}
	return { status: 'timeout', durationMs: Date.now() - start };
}

function sleep(ms: number) {
	return new Promise((r) => setTimeout(r, ms));
}

/**
 * Pull an HTTP status code off a thrown error. Sisense's HttpClient surfaces
 * non-2xx responses as Errors with translation-key messages and the real
 * status under `interpolationOptions.status`; other clients/adapters may put
 * it on `status`, `statusCode`, or `response.status`. Returns undefined when
 * the shape doesn't match any of those (network errors, JSON parse errors).
 */
function extractHttpStatus(err: unknown): number | undefined {
	if (!err || typeof err !== 'object') return undefined;
	const e = err as Record<string, unknown>;
	const candidates: unknown[] = [
		e.status,
		e.statusCode,
		(e.response as Record<string, unknown> | undefined)?.status,
		(e.interpolationOptions as Record<string, unknown> | undefined)?.status,
	];
	for (const c of candidates) {
		const n = typeof c === 'number' ? c : Number(c);
		if (Number.isFinite(n) && n > 0) return n;
	}
	return undefined;
}
